import React, { FC, useState } from 'react';
import { SectionPanel } from './browserLadder/SectionPanel';
import { useLadderSection } from './browserLadder/useLadderSection';
import {
  BrowsingOutcomes,
  HumanWork,
  IntentOutcomes,
} from './browserLadder/outcomeSections';
import type { Granularity } from './browserLadder/series';
import {
  CaptchaAutonomy,
  SecureInputAuth,
} from './browserLadder/autonomySections';
import './scaling-dashboard.scss';

/**
 * Browser Ladder tab of Platform Stats: five impact sections answering
 * "is browser automation succeeding more, faster, with less human work?".
 * Each section is fetched from GET /platform-stats/browser-ladder?section=…
 * on its own, so one slow query never blanks the page. Tiles read the last
 * COMPLETE week with a week-over-week delta and the pre-ladder baseline;
 * charts switch between day-over-day and week-over-week.
 */

const WEEK_RANGES = [4, 8, 13, 26] as const;

const BrowserLadderDashboard: FC = () => {
  const [weeks, setWeeks] = useState<number>(13);
  const [granularity, setGranularity] = useState<Granularity>('weekly');

  const intents = useLadderSection('intents', weeks);
  const browsing = useLadderSection('browsing', weeks);
  const captcha = useLadderSection('captcha', weeks);
  const secureInput = useLadderSection('secureInput', weeks);

  return (
    <div className='scaling-dashboard browser-ladder'>
      <div className='scaling-controls'>
        <p className='scaling-intro'>
          Is browser automation succeeding more often, faster, and with less
          human work since the browser ladder went live? Headline numbers are
          the last complete week; dashed red lines mark releases.
        </p>
        <div className='day-selector' aria-label='Range in weeks'>
          {WEEK_RANGES.map((w) => (
            <button
              key={w}
              className={`day-btn ${weeks === w ? 'active' : ''}`}
              onClick={() => setWeeks(w)}
            >
              {w}w
            </button>
          ))}
        </div>
        <div className='day-selector' aria-label='Chart granularity'>
          <button
            className={`day-btn ${granularity === 'daily' ? 'active' : ''}`}
            onClick={() => setGranularity('daily')}
          >
            Day over day
          </button>
          <button
            className={`day-btn ${granularity === 'weekly' ? 'active' : ''}`}
            onClick={() => setGranularity('weekly')}
          >
            Week over week
          </button>
        </div>
      </div>

      <SectionPanel
        index={1}
        title='Intent outcomes'
        question='Are browser intents (sub-threads) succeeding more often, and how quickly?'
        loading={intents.loading}
        warming={intents.warming}
        error={intents.error}
        onRetry={intents.retry}
      >
        {intents.data && (
          <IntentOutcomes data={intents.data} granularity={granularity} />
        )}
      </SectionPanel>

      <SectionPanel
        index={2}
        title='Browsing outcomes'
        question='Do browser tasks finish instead of getting stuck, and how long do they take?'
        loading={browsing.loading}
        warming={browsing.warming}
        error={browsing.error}
        onRetry={browsing.retry}
      >
        {browsing.data && (
          <BrowsingOutcomes data={browsing.data} granularity={granularity} />
        )}
      </SectionPanel>

      <SectionPanel
        index={3}
        title='Human work'
        question='Are web actions and browser interventions falling without hurting intent success?'
        loading={intents.loading}
        warming={intents.warming}
        error={intents.error}
        onRetry={intents.retry}
      >
        {intents.data && (
          <HumanWork data={intents.data} granularity={granularity} />
        )}
      </SectionPanel>

      <SectionPanel
        index={4}
        title='CAPTCHA autonomy'
        question='How many CAPTCHAs are machines solving, and how many still reach a human?'
        loading={captcha.loading}
        warming={captcha.warming}
        error={captcha.error}
        onRetry={captcha.retry}
      >
        {captcha.data && (
          <CaptchaAutonomy data={captcha.data} granularity={granularity} />
        )}
      </SectionPanel>

      <SectionPanel
        index={5}
        title='Secure Input (M2M login)'
        question='Are logins completing machine-to-machine, and is human login work falling?'
        loading={secureInput.loading}
        warming={secureInput.warming}
        error={secureInput.error}
        onRetry={secureInput.retry}
      >
        {secureInput.data && (
          <SecureInputAuth data={secureInput.data} granularity={granularity} />
        )}
      </SectionPanel>
    </div>
  );
};

export default BrowserLadderDashboard;
